import { useGyymClient } from '@gyym/sdk-react'
import {
  Button,
  CheckboxField,
  cn,
  Form,
  Link,
  TextField,
  useDialog,
} from '@gyym/ui'
import { Document } from '@gyym/ui/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/nextjs'
import React from 'react'
import { useController, useForm, type UseFormSetError } from 'react-hook-form'
import { z } from 'zod'

import { CAMPAIGN_CODE_REGEX, EMAIL_REGEX, getFieldErrors } from '@/lib/form'

import { CampaignDialogBody } from './CampaignDialogBody'

export const schema = z
  .object({
    email: z
      .string()
      .min(1, { message: 'メールアドレスを入力してください' })
      .regex(EMAIL_REGEX, { message: '有効なメールアドレスではありません' }),
    password: z.union([
      z
        .string()
        .min(1, { message: 'パスワードを入力してください' })
        .min(10, { message: '10文字以上で入力してください' })
        .max(30, { message: '30文字以内で入力してください' })
        .regex(/[0-9]/, {
          message: '数字を含めてください',
        })
        .regex(/[!-/:-@[-`{-~]/, {
          message: '記号を含めてください',
        })
        .refine((value) => /[A-Z]/.test(value) && /[a-z]/.test(value), {
          message: '英大文字、英小文字を含めてください',
        }),
      // Allow undefined for SNS registration
      z.undefined(),
    ]),
    passwordConfirmation: z.union([
      z.string(),
      // Allow undefined for SNS registration
      z.undefined(),
    ]),
    campaignCode: z.union([
      z.literal(''),
      z.string().regex(CAMPAIGN_CODE_REGEX, {
        message: '有効なキャンペーンコードではありません',
      }),
    ]),
    notification: z.boolean(),
  })
  .refine(
    ({ password, passwordConfirmation }) => password === passwordConfirmation,
    {
      message: 'パスワードが一致しません',
      path: ['passwordConfirmation'],
    },
  )

export type FormValues = z.infer<typeof schema>

export type SubmitContext = {
  setError: UseFormSetError<FormValues>
}

export interface SignupFormProps {
  /**
   * Initial form values
   */
  values?: Partial<FormValues>

  /**
   * Whether the form is for SNS registration
   */
  isSnsRegistration?: boolean

  /**
   * Submit handler
   */
  onSubmit: (values: FormValues, ctx: SubmitContext) => void

  /**
   * Children render prop
   */
  children?: ({
    fields,
    submit,
  }: {
    fields: React.ReactNode
    submit: React.ReactNode
  }) => React.ReactNode
}

let didCheckCampaignCode = false

export const SignupForm = ({
  values,
  isSnsRegistration,
  onSubmit,
  children,
}: SignupFormProps) => {
  const client = useGyymClient()
  const { openDialog } = useDialog()
  const hasChildren = !!children

  const {
    register,
    control,
    watch,
    setError,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormValues>({
    // Validation will trigger on the first blur event.
    // After the first blur event, validation will trigger on every change event.
    mode: 'onTouched',
    // Get all validation errors for each field
    criteriaMode: 'all',
    resolver: zodResolver(schema),
    defaultValues: values,
  })

  const onSubmitHandler = React.useCallback(
    (values: FormValues) => onSubmit(values, { setError }),
    [onSubmit, setError],
  )

  const { field: notificationField } = useController({
    name: 'notification',
    control,
    defaultValue: true,
  })

  const campaignCode = watch('campaignCode', values?.campaignCode)
  const [isConfirmingCampaignCode, setIsConfirmingCampaignCode] =
    React.useState(false)

  const validateCampaignCode = React.useCallback(
    async (code: string) => {
      // Check empty and invalid code
      if (!code || !CAMPAIGN_CODE_REGEX.test(code)) return

      // Check campaign code is confirming
      if (isConfirmingCampaignCode) return

      // Check campaign code validity
      try {
        setIsConfirmingCampaignCode(true)
        const campaign = await client.user.campaigns.getByCode(code)

        if (!campaign) return

        openDialog({
          title: 'キャンペーン概要',
          body: <CampaignDialogBody campaign={campaign} />,
        })
      } catch (e) {
        if (!client.isHttpError(e)) {
          Sentry.captureException(e)
          return
        }

        if (e.status !== 400) {
          Sentry.captureException(e)
        }

        setError('campaignCode', {
          type: 'custom',
          message:
            e.status === 400 && e.message
              ? e.message
              : 'キャンペーンを取得できませんでした。',
        })
      } finally {
        setIsConfirmingCampaignCode(false)
      }
    },
    [client, isConfirmingCampaignCode, setError, openDialog],
  )

  const onCampaignCodeBlur = () => {
    validateCampaignCode(campaignCode)
  }

  // Check campaign code passed in by values prop
  React.useEffect(() => {
    if (!didCheckCampaignCode && values?.campaignCode) {
      didCheckCampaignCode = true
      validateCampaignCode(values?.campaignCode)
    }
  }, [values?.campaignCode, validateCampaignCode])

  const fields = (
    <>
      <TextField
        label="メールアドレス"
        placeholder="example@gyym.jp"
        {...register('email')}
        disabled={Boolean(values && values.email && isSnsRegistration)}
        errorMessages={errors.email?.message}
        autoComplete="email"
      />

      {!isSnsRegistration && (
        <>
          <TextField
            label="パスワード"
            placeholder="10文字以上、30文字以内"
            type="password"
            {...register('password')}
            errorMessages={getFieldErrors(errors.password)}
            autoComplete="new-password"
          />

          <TextField
            label="パスワード確認用"
            type="password"
            {...register('passwordConfirmation')}
            errorMessages={errors.passwordConfirmation?.message}
            autoComplete="new-password"
          />
        </>
      )}

      <TextField
        label="キャンペーンコード"
        placeholder="コードを入力（任意）"
        {...register('campaignCode', {
          onBlur: onCampaignCodeBlur,
        })}
        captionMessages="プロフィール登録後にキャンペーンポイントが付与されます。"
        errorMessages={errors.campaignCode?.message}
      />

      <CheckboxField
        label="GYYMからのお知らせメールを受け取る"
        {...notificationField}
        value={undefined}
        checked={notificationField.value}
        errorMessages={errors.notification?.message}
      />
    </>
  )

  const submit = (
    <div className="flex w-full flex-col items-center gap-10">
      <Button variant="primary-text" size="sm" asChild>
        <Link href="/term" target="_blank">
          <Document />
          GYYM利用規約
        </Link>
      </Button>

      <Button
        className={cn({
          'mt-6': !hasChildren,
        })}
        type="submit"
        variant="brand"
        size="lg"
        fullWidth
        loading={isSubmitting || isConfirmingCampaignCode}
        disabled={!isValid}
      >
        利用規約に同意して登録
      </Button>
    </div>
  )

  return (
    <Form gap={!hasChildren ? 4 : 0} onSubmit={handleSubmit(onSubmitHandler)}>
      {hasChildren ? (
        children({ fields, submit })
      ) : (
        <>
          {fields}
          <div className="mt-4 flex w-full self-center tablet:max-w-[382px]">
            {submit}
          </div>
        </>
      )}
    </Form>
  )
}
